import { alpha, Box, IconButton } from '@mui/material';
import { FlashlightOff, FlashlightOn } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';
import { useTranslation } from 'react-i18next';

type ScannerViewProps = {
  paused: boolean;
  onScan: (qr: string) => void;
  onPermissionDenied: () => void;
  torchOn: boolean;
  onTorchChange: (on: boolean) => void;
};

const ScannerView = ({ paused, onScan, onPermissionDenied, torchOn, onTorchChange }: ScannerViewProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [torchAvailable, setTorchAvailable] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const scanner = new QrScanner(
      video,
      (result) => {
        const text = typeof result === 'string' ? result : result.data;
        if (text) onScanRef.current(text);
      },
      {
        preferredCamera: 'environment',
        highlightScanRegion: false,
        returnDetailedScanResult: true,
      },
    );
    scannerRef.current = scanner;

    scanner
      .start()
      .then(async () => {
        const hasFlash = await scanner.hasFlash();
        setTorchAvailable(hasFlash);
      })
      .catch(() => {
        onPermissionDenied();
      });

    return () => {
      scanner.stop();
      scanner.destroy();
      scannerRef.current = null;
    };
  }, [onPermissionDenied]);

  useEffect(() => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    if (paused) {
      scanner.stop();
    } else {
      scanner.start().catch(() => onPermissionDenied());
    }
  }, [paused, onPermissionDenied]);

  const handleToggleTorch = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      if (torchOn) {
        await scanner.turnFlashOff();
        onTorchChange(false);
      } else {
        await scanner.turnFlashOn();
        onTorchChange(true);
      }
    } catch {
      /* torch not supported */
    }
  };

  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {torchAvailable && (
        <IconButton
          onClick={handleToggleTorch}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            insetInlineEnd: 8,
            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.5),
            color: 'common.white',
            '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.7) },
          }}
          aria-label={t('checkin_scanner.torch')}>
          {torchOn ? <FlashlightOn fontSize="small" /> : <FlashlightOff fontSize="small" />}
        </IconButton>
      )}
    </Box>
  );
};

export default ScannerView;
